import { create } from "zustand";
import { TradingAccount, TradingAccountInput, AccountsStore } from "./types";
import {
  getStoredAccounts,
  saveAccounts,
  getStoredActiveAccount,
  saveActiveAccount,
  clearStoredAccounts,
  clearStoredActiveAccountId,
} from "./storage";
import { clearDesk } from "@/lib/chart-desk/storage";
import { useGatewaysStore } from "@/lib/gateways/store";
import { CUSTOM_GATEWAY_SLUG_PREFIX } from "@/lib/gateways/types";

export const useAccountsStore = create<AccountsStore>((set, get) => ({
  accounts: [],
  activeAccountId: null,
  reconnectNonce: 0,

  requestReconnect: () => {
    if (!get().activeAccountId) return;
    set((state) => ({ reconnectNonce: state.reconnectNonce + 1 }));
  },

  addAccount: (accountInput: TradingAccountInput) => {
    const { accounts } = get();
    
    // Generate unique ID
    const id = `${accountInput.companyName}_${accountInput.credentials.login}_${Date.now()}`;
    const account: TradingAccount = { ...accountInput, id };
    
    const updatedAccounts = [...accounts, account];
    
    // Update store
    set({ accounts: updatedAccounts });
    
    // Save to localStorage
    saveAccounts(updatedAccounts);
    
    // Always set newly added account as active
    get().setActiveAccountId(account.id);
  },

  updateAccount: (updatedAccount: TradingAccount) => {
    const { accounts, activeAccountId } = get();
    const updatedAccounts = accounts.map((account) =>
      account.id === updatedAccount.id ? updatedAccount : account
    );
    
    // Update accounts
    set({ accounts: updatedAccounts });
    
    // Save to localStorage
    saveAccounts(updatedAccounts);
    
    // If the updated account is the active one, save it
    if (activeAccountId === updatedAccount.id) {
      saveActiveAccount(updatedAccount);
    }
  },

  deleteAccount: (id: string) => {
    const { accounts, activeAccountId } = get();
    const updatedAccounts = accounts.filter((account) => account.id !== id);
    
    // If the deleted account was active, clear activeAccountId
    if (activeAccountId === id) {
      set({ 
        accounts: updatedAccounts,
        activeAccountId: null 
      });
      saveActiveAccount(null);
    } else {
      // Only update accounts
      set({ accounts: updatedAccounts });
    }
    
    // Save to localStorage
    saveAccounts(updatedAccounts);
    
    // If no accounts left, clear all storage
    if (updatedAccounts.length === 0) {
      clearStoredAccounts();
      clearStoredActiveAccountId();
    }
  },

  setActiveAccountId: (accountId: string | null) => {
    // Proposal 74: the chart desk is scoped to one account. Switching to a
    // *different* account drops it so the new account starts clean and can
    // never inherit symbols from another gateway. Re-selecting the same
    // account (reconnect) must leave it alone.
    const previousAccountId = get().activeAccountId;
    if (previousAccountId && previousAccountId !== accountId) {
      clearDesk();
    }

    if (accountId) {
      const { accounts } = get();
      const account = accounts.find(acc => acc.id === accountId);
      
      if (account) {
        // Update lastConnectedAt timestamp
        const updatedAccount = {
          ...account,
          lastConnectedAt: Date.now(),
        };
        
        // Update accounts array with new timestamp
        const updatedAccounts = accounts.map((acc) =>
          acc.id === updatedAccount.id ? updatedAccount : acc
        );
        
        // Update activeAccountId and accounts. Re-selecting the account that is ALREADY active is the
        // user asking to reconnect it — the id alone would not change, so bump the nonce that
        // useAccountConnection also watches.
        set((state) => ({
          accounts: updatedAccounts,
          activeAccountId: accountId,
          reconnectNonce:
            accountId === previousAccountId ? state.reconnectNonce + 1 : state.reconnectNonce,
        }));
        
        // Save to localStorage
        saveAccounts(updatedAccounts);
        saveActiveAccount(updatedAccount);
      }
    } else {
      // If clearing active account
      set({ activeAccountId: null });
      saveActiveAccount(null);
    }
  },

  purgeSyncedAccounts: () => {
    const { accounts, activeAccountId } = get();
    const kept = accounts.filter((account) => account.origin !== "vybe");
    if (kept.length === accounts.length) return;

    // If the account being disconnected was the active one, clear the selection FIRST: that is what
    // `useAccountConnection` reacts to, so it is what actually tears the hub connection down. Leaving it
    // selected would keep a session-derived account streaming after the session that authorised it ended.
    const activeWasSynced =
      !!activeAccountId && !kept.some((account) => account.id === activeAccountId);
    if (activeWasSynced) {
      set({ accounts: kept, activeAccountId: null });
      saveActiveAccount(null);
    } else {
      set({ accounts: kept });
    }

    saveAccounts(kept);
    if (kept.length === 0) {
      clearStoredAccounts();
      clearStoredActiveAccountId();
    }
  },

  loadAccounts: () => {
    const storedAccounts = getStoredAccounts().map((account) => ({
      ...account,
      providerKind: account.providerKind ?? "T5",
    }));
    const storedActiveAccount = getStoredActiveAccount();
    
    // Validate that active account still exists in accounts list
    let validActiveAccountId = null;
    if (storedActiveAccount) {
      const accountExists = storedAccounts.find(
        (acc) => acc.id === storedActiveAccount.id
      );
      if (accountExists) {
        validActiveAccountId = storedActiveAccount.id;
      }
    }
    
    // Update store
    set({
      accounts: storedAccounts,
      activeAccountId: validActiveAccountId,
    });
    
    // If stored active account was invalid, clear it from storage
    if (storedActiveAccount && !validActiveAccountId) {
      saveActiveAccount(null);
    }
  },
}));

/**
 * Human gateway label for an account. Resolved from the account's GATEWAY first
 * (its CRM slug → compact protocol name, else the catalog / custom-gateway name),
 * and only then from the legacy `providerKind`. Gateway-bound accounts are stored
 * with `providerKind: "T5"` regardless of gateway, so the old providerKind-only
 * lookup labelled every ViiSync-gateway account "MT5" (SR Global bug 2026-09-08).
 */
export const getGatewayLabel = (
  account: Pick<TradingAccount, "providerKind" | "gatewaySlug"> | null | undefined
): string => {
  const slug = account?.gatewaySlug;
  if (slug) {
    const gw = useGatewaysStore.getState();
    if (slug.startsWith(CUSTOM_GATEWAY_SLUG_PREFIX)) {
      const custom = gw.customGateways.find(
        (g) => g.id === slug.slice(CUSTOM_GATEWAY_SLUG_PREFIX.length)
      );
      if (custom?.name) return custom.name;
    } else {
      // Compact protocol badge from the slug convention (sig-viisync / sig-mt5 / sig-vybe) —
      // works even before the gateway catalog has loaded; catalog name is the fallback.
      if (/viisync/i.test(slug)) return "ViiSync";
      if (/mt5/i.test(slug)) return "MT5";
      if (/vybe/i.test(slug)) return "Vybe";
      const known = gw.getBySlug(slug);
      if (known?.name) return known.name;
    }
  }
  return account?.providerKind === "Vybe" ? "ViiSync" : "MT5";
};

// Helper hook to get active account
export const useActiveAccount = () => {
  const activeAccountId = useAccountsStore((state) => state.activeAccountId);
  const accounts = useAccountsStore((state) => state.accounts);
  
  return activeAccountId 
    ? accounts.find(acc => acc.id === activeAccountId) || null
    : null;
};
